// utils/documento.ts
//
// Fonte única de máscara e validação de CPF e CNPJ do projeto.
//
// O CNPJ segue o NOVO MODELO ALFANUMÉRICO da Receita Federal (vigente a partir
// de julho/2026): as 12 primeiras posições (raiz + ordem) podem conter letras
// (A-Z) e números, e os 2 dígitos verificadores finais permanecem numéricos.
// O cálculo dos DVs usa módulo 11 sobre o valor de cada caractere, obtido pelo
// seu código ASCII menos 48 (assim '0'..'9' => 0..9 e 'A'..'Z' => 17..42).
// O algoritmo é retrocompatível com o CNPJ puramente numérico legado.

export const CNPJ_LENGTH = 14;
export const CPF_LENGTH = 11;

// Máscaras para o componente InputMask do PrimeReact.
// "*" aceita caractere alfanumérico e "9" aceita apenas dígito.
export const CNPJ_INPUT_MASK = "**.***.***/****-99";
export const CPF_INPUT_MASK = "999.999.999-99";
export const CNPJ_PLACEHOLDER = "AA.AAA.AAA/AAAA-99";

const toText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

/**
 * Remove a formatação do CNPJ preservando letras (novo modelo alfanumérico).
 * As 12 primeiras posições ficam alfanuméricas e as 2 últimas (DV) numéricas.
 */
export const cleanCnpj = (value: string): string => {
  const raw = toText(value).toUpperCase().replace(/[^0-9A-Z]/g, "");
  const base = raw.slice(0, 12);
  const dv = raw.slice(12, CNPJ_LENGTH).replace(/[^0-9]/g, "");
  return base + dv;
};

/** Remove tudo que não é dígito (o CPF é sempre numérico). */
export const cleanCpf = (value: string): string =>
  toText(value).replace(/\D/g, "");

/** Indica se o conteúdo deve ser tratado como CNPJ (tem letra ou mais de 11 dígitos). */
export const isCnpjValue = (value: string): boolean => {
  const text = toText(value);
  return /[A-Za-z]/.test(text) || text.replace(/\D/g, "").length > CPF_LENGTH;
};

/** Aplica a máscara do CNPJ alfanumérico de forma incremental: AA.AAA.AAA/AAAA-99 */
export const formatCnpj = (value: string): string => {
  const chars = cleanCnpj(value);
  let out = "";
  for (let i = 0; i < chars.length; i++) {
    if (i === 2 || i === 5) out += ".";
    else if (i === 8) out += "/";
    else if (i === 12) out += "-";
    out += chars.charAt(i);
  }
  return out;
};

/** Aplica a máscara do CPF de forma incremental: 000.000.000-00 */
export const formatCpf = (value: string): string => {
  const chars = cleanCpf(value).slice(0, CPF_LENGTH);
  let out = "";
  for (let i = 0; i < chars.length; i++) {
    if (i === 3 || i === 6) out += ".";
    else if (i === 9) out += "-";
    out += chars.charAt(i);
  }
  return out;
};

/** Máscara combinada para campos que aceitam CPF ou CNPJ. */
export const maskCpfCnpj = (value: string): string =>
  isCnpjValue(value) ? formatCnpj(value) : formatCpf(value);

/** Formata um documento já persistido (CPF ou CNPJ) para exibição. */
export const formatCpfCnpj = maskCpfCnpj;

// Cálculo de um dígito verificador do CNPJ (módulo 11) a partir do valor de
// cada caractere, percorrendo da direita para a esquerda com peso 2..9 cíclico.
const calcCnpjDv = (values: number[]): number => {
  let soma = 0;
  let peso = 2;
  for (let i = values.length - 1; i >= 0; i--) {
    soma += values[i] * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
};

/** Valida CNPJ no novo modelo alfanumérico (compatível com o CNPJ numérico legado). */
export const isValidCnpj = (value: string): boolean => {
  const cnpj = cleanCnpj(value);

  if (cnpj.length !== CNPJ_LENGTH) return false;

  // Rejeita repetição do mesmo caractere (ex.: 00000000000000).
  if (/^(.)\1{13}$/.test(cnpj)) return false;

  const base = cnpj.slice(0, 12);
  const dvInformado = cnpj.slice(12);

  // Os dígitos verificadores são sempre numéricos.
  if (!/^\d{2}$/.test(dvInformado)) return false;

  // Valor de cada caractere: código ASCII − 48 ('0'->0 … 'A'->17 … 'Z'->42).
  const valores = base.split("").map((c) => c.charCodeAt(0) - 48);

  const dv1 = calcCnpjDv(valores);
  const dv2 = calcCnpjDv(valores.concat(dv1));

  return (
    dv1 === Number(dvInformado.charAt(0)) &&
    dv2 === Number(dvInformado.charAt(1))
  );
};

/** Valida CPF (módulo 11). */
export const isValidCpf = (value: string): boolean => {
  const cpf = cleanCpf(value);
  if (cpf.length !== CPF_LENGTH || /^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i), 10) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.charAt(9), 10)) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i), 10) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.charAt(10), 10)) return false;

  return true;
};

/** Valida CPF ou CNPJ conforme o conteúdo informado. */
export const isValidCpfCnpj = (value: string): boolean =>
  isCnpjValue(value) ? isValidCnpj(value) : isValidCpf(value);
